import React, { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  InputNumber,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  AppstoreOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useApplicationStore } from '../store';
import { useModal } from '../hooks';
import SearchBar from '../components/common/SearchBar';
import EmptyState from '../components/common/EmptyState';
import { formatDate } from '../utils';

const { Option } = Select;

const Applications: React.FC = () => {
  const {
    applicationList,
    createApplication,
    updateApplication,
    deleteApplication,
    fetchApplicationList,
  } = useApplicationStore();
  const modal = useModal();
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [editingApp, setEditingApp] = useState<any>(null);

  useEffect(() => {
    fetchApplicationList();
  }, [fetchApplicationList]);

  // 处理保存
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      modal.startConfirmLoading();

      if (editingApp) {
        await updateApplication(editingApp.id, values);
        message.success('应用更新成功');
      } else {
        await createApplication(values);
        message.success('应用创建成功');
      }

      modal.hideModal();
      form.resetFields();
      setEditingApp(null);
    } catch (error) {
      console.error('操作失败:', error);
      modal.stopConfirmLoading();
    }
  };

  // 显示新增/编辑模态框
  const showCreateModal = () => {
    setEditingApp(null);
    form.resetFields();
    modal.showModal();
  };

  const showEditModal = (app: any) => {
    setEditingApp(app);
    form.setFieldsValue(app);
    modal.showModal();
  };

  // 删除应用
  const handleDelete = async (app: any) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除应用 "${app.name}" 吗？`,
      okText: '确定',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await deleteApplication(app.id);
          message.success('应用删除成功');
        } catch (error) {
          console.error('删除失败:', error);
        }
      },
    });
  };

  // 启停应用
  const handleToggleStatus = async (app: any) => {
    const newStatus = app.status === 'active' ? 'inactive' : 'active';
    const actionText = newStatus === 'active' ? '启动' : '停止';

    Modal.confirm({
      title: `确认${actionText}`,
      content: `确定要${actionText}应用 "${app.name}" 吗？`,
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          await updateApplication(app.id, { status: newStatus });
          message.success(`${actionText}成功`);
        } catch (error) {
          console.error('操作失败:', error);
        }
      },
    });
  };

  // 筛选应用
  const filteredApps = applicationList.filter(app =>
    app.name.toLowerCase().includes(searchText.toLowerCase()) ||
    app.description.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: '📱 应用名称',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: '📝 描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '📊 状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusMap: Record<string, { color: string; text: string }> = {
          active: { color: 'success', text: '✅ 运行中' },
          inactive: { color: 'error', text: '⏸️ 已停止' },
          pending: { color: 'warning', text: '⏳ 待审核' },
        };
        const { color, text } = statusMap[status] || { color: 'default', text: status };
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: '⚙️ 操作',
      key: 'action',
      width: 200,
      render: (_: any, record: any) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => showEditModal(record)}
          >
            编辑
          </Button>
          <Button
            type="link"
            size="small"
            icon={record.status === 'active' ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
            onClick={() => handleToggleStatus(record)}
          >
            {record.status === 'active' ? '停止' : '启动'}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '0 24px 24px' }}>
      <Card
        title={
          <Space>
            <AppstoreOutlined />
            <span>应用管理</span>
          </Space>
        }
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={showCreateModal}>
            新增应用
          </Button>
        }
        bordered={false}
        style={{ borderRadius: 8 }}
      >
        <div style={{ marginBottom: 16 }}>
          <SearchBar
            placeholder="搜索应用名称或描述..."
            onSearch={setSearchText}
            width={300}
          />
        </div>
        <Table
          dataSource={filteredApps}
          columns={columns}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          size="middle"
          locale={{ emptyText: <EmptyState description="暂无应用数据" /> }}
        />
      </Card>

      <Modal
        title={editingApp ? '✏️ 编辑应用' : '➕ 新增应用'}
        open={modal.visible}
        onOk={handleSave}
        onCancel={modal.handleCancel}
        confirmLoading={modal.confirmLoading}
        okText="保存"
        cancelText="取消"
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="📱 应用名称"
            rules={[{ required: true, message: '请输入应用名称!' }]}
          >
            <Input placeholder="例如：电商平台、移动APP等" />
          </Form.Item>

          <Form.Item
            name="description"
            label="📝 应用描述"
            rules={[{ required: true, message: '请输入应用描述!' }]}
          >
            <Input.TextArea
              rows={3}
              placeholder="简要描述应用的功能和用途"
              showCount
              maxLength={200}
            />
          </Form.Item>

          <Form.Item
            name="type"
            label="🏷️ 应用类型"
            rules={[{ required: true, message: '请选择应用类型!' }]}
          >
            <Select placeholder="请选择应用类型">
              <Option value="web">🌐 Web应用</Option>
              <Option value="mobile">📱 移动应用</Option>
              <Option value="api">🔌 API服务</Option>
              <Option value="desktop">💻 桌面应用</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="status"
            label="📊 初始状态"
            rules={[{ required: true, message: '请选择初始状态!' }]}
          >
            <Select placeholder="请选择初始状态">
              <Option value="active">✅ 运行中</Option>
              <Option value="inactive">⏸️ 已停止</Option>
              <Option value="pending">⏳ 待审核</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Applications;
